/**
 * Proximity scroll-snap. Once scroll velocity drops near a section threshold,
 * smoothly tweens the page to fill the screen with that section.
 *
 * Snap targets are offset by the nav's height so a section's top doesn't end
 * up hidden behind the fixed nav bar.
 */

const SECTION_SELECTORS = ['.s-hero', '#work', '#stack', '#process', '#contact', '.s-footer'];

// Asymmetric snap windows:
//  - BEFORE: user is above the section's snap point and scrolling down toward
//    it. We give a generous window so the section pulls into place from far.
//  - AFTER: user is below the snap point. Smaller window so we don't yank
//    them back upward unless they're really close.
const SNAP_WINDOW_BEFORE = 340;
const SNAP_WINDOW_AFTER  = 110;
const VEL_THRESHOLD = 0.18;    // px/ms — wait until the scroll has clearly settled
const IDLE_MS = 220;           // longer idle before considering a snap
const SNAP_DUR = 550;
const SNAP_COOLDOWN_MS = 900;  // refuse to snap again right after a snap finishes

interface SnapTarget {
  el: HTMLElement;
  top: number;
  height: number;
}

const navOffset = (): number => {
  const nav = document.querySelector<HTMLElement>('.s-nav');
  return nav ? Math.max(0, nav.getBoundingClientRect().bottom) : 0;
};

/**
 * Decide where scrollY should land for each kind of snap target.
 *
 * - Alt sections (full-bleed flare "rupture") fill the visible-below-nav
 *   area; we top-align the section edge to the nav bottom.
 * - Regular sections are anchored on their __head element so the big
 *   numeral sits just below the nav with a small comfort gap. The
 *   section's top padding tucks behind the nav.
 * - Sections without a head (hero, footer): top-align if they fill the
 *   viewport, otherwise center in the visible area below the nav.
 */
const COMFORT_GAP = 32; // breathing room between nav bottom and section header

const snapPointFor = (t: SnapTarget): number => {
  const offset = navOffset();

  if (t.el.classList.contains('s-section--alt')) {
    return t.top - offset;
  }

  const head = t.el.querySelector<HTMLElement>('.s-section__head');
  if (head) {
    const headTop = head.getBoundingClientRect().top + window.scrollY;
    return headTop - offset - COMFORT_GAP;
  }

  const visible = Math.max(0, window.innerHeight - offset);
  if (t.height >= visible) return t.top - offset;
  const slack = (visible - t.height) / 2;
  return t.top - offset - slack;
};

export function setupProximitySnap() {
  // Skip snap on touch devices — momentum scrolling on iOS/Android plus the
  // browser's own snap heuristics produce a better feel natively. Snap is for
  // mouse-wheel & trackpad users, where the native behaviour can stop
  // anywhere mid-section.
  if (matchMedia('(pointer: coarse)').matches) return;
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  let snapping = false;
  let lastSnapEnd = 0;

  const collect = (): SnapTarget[] =>
    SECTION_SELECTORS
      .map((s) => document.querySelector<HTMLElement>(s))
      .filter((el): el is HTMLElement => el != null)
      .map((el) => {
        const r = el.getBoundingClientRect();
        return { el, top: r.top + window.scrollY, height: r.height };
      });

  let targets = collect();
  window.addEventListener('resize', () => { targets = collect(); }, { passive: true });

  const tween = (toY: number, dur = SNAP_DUR) => {
    snapping = true;
    const fromY = window.scrollY;
    const dy = toY - fromY;
    if (Math.abs(dy) < 2) { snapping = false; return; }
    const t0 = performance.now();
    const ease = (t: number) => 1 - Math.pow(1 - t, 3);
    const step = () => {
      const t = Math.min(1, (performance.now() - t0) / dur);
      window.scrollTo(0, fromY + dy * ease(t));
      if (t < 1) requestAnimationFrame(step);
      else {
        snapping = false;
        lastSnapEnd = performance.now();
      }
    };
    requestAnimationFrame(step);
  };

  const considerSnap = () => {
    if (snapping) return;
    if (performance.now() - lastSnapEnd < SNAP_COOLDOWN_MS) return;
    targets = collect();
    const y = window.scrollY;
    let best: SnapTarget | null = null;
    let bestPoint = 0;
    let bestDist = Infinity;
    for (const t of targets) {
      const point = snapPointFor(t);
      const signed = point - y;             // positive = header is below us
      const d = Math.abs(signed);
      const window = signed >= 0 ? SNAP_WINDOW_BEFORE : SNAP_WINDOW_AFTER;
      if (d < window && d < bestDist) {
        best = t;
        bestPoint = point;
        bestDist = d;
      }
    }
    if (!best) return;
    if (bestDist < 4) return;
    tween(bestPoint);
  };

  let lastY = window.scrollY;
  let lastT = performance.now();
  let idleTimer: ReturnType<typeof setTimeout> | null = null;

  window.addEventListener('scroll', () => {
    const now = performance.now();
    const dt = now - lastT || 16;
    const velocity = (window.scrollY - lastY) / dt;
    lastY = window.scrollY;
    lastT = now;
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
      if (Math.abs(velocity) < VEL_THRESHOLD) considerSnap();
    }, IDLE_MS);
  }, { passive: true });

  // anchor link tween — also offset by nav height so the heading isn't covered
  document.addEventListener('click', (e) => {
    const target = e.target as Element | null;
    const a = target?.closest?.('a[href^="#"]') as HTMLAnchorElement | null;
    if (!a) return;
    const id = a.getAttribute('href')?.slice(1);
    if (!id) return;
    const el = document.getElementById(id);
    if (!el) return;
    e.preventDefault();
    const r = el.getBoundingClientRect();
    const t: SnapTarget = { el, top: r.top + window.scrollY, height: r.height };
    tween(snapPointFor(t), 750);
  });
}
